import { getApiChatBotDataGet, getApiEmbeddingCollectionGet, postApiChatBotDataConnectEmbeddingCollection, postApiChatBotDataCreateAndConnectEmbeddingCollection, postApiChatBotDataExport, useGetApiChatBotDataGet, useGetApiEmbeddingCollectionList, useGetApiWorkspaceGet, usePutApiChatBotDataUpdateChatConfig, usePutApiChatBotDataUpdateTagsConfig, usePutApiChatBotDataUpdateTestConfig } from "@/API"
import { ActionIcon, Box, Breadcrumbs, Button, ComboboxItem, Group, LoadingOverlay, Menu, Stack, Text, Tooltip } from "@mantine/core"
import { modals } from "@mantine/modals"
import { notifications } from "@mantine/notifications"
import { useQueryClient } from "@tanstack/react-query"
import { Link, useNavigate, useParams } from "@tanstack/react-router"
import { IconDatabasePlus, IconDatabaseShare, IconDots, IconEdit, IconFileExport, IconFlask, IconMessageReport, IconMessages, IconTags, IconTrash } from "@tabler/icons-react";
import { ChatConfigEditButton } from "./ChatConfigEditButton";
import { ChatBotEmbeddingCollectionEditItem } from "./EmbeddingCollectionEditItem";
import { useMemo } from "react";
import { TagsConfigEditButton } from "./TagsConfigEditButton";
import { PageTitle } from "@/Components/PageTitle";
import { TestConfigEditButton } from "./TestConfigEditButton"
import { LogTokenUsageData, SumTokenUsageData } from "@/Helpers/TokenUsageHelper"
import { TooltipMultiline } from "@/Components/TooltipMultiline"
import { TextSaveToFile } from "@/Helpers/SaveToFile"
import { useDeleteChatBotData } from "./Helpers/DeleteChatBotData"
import { useRenameChatBotData } from "./Helpers/RenameChatBotData"
import { AppBreadcrumbs } from "@/Components/AppBreadcrumbs"

export function ChatBotDataPage() {
    const { workspaceId, chatBotDataId } = useParams({ from: "/workspace/$workspaceId/chatBotData/$chatBotDataId/" })
    const queryClient = useQueryClient()
    const navigate = useNavigate()
    const workspace = useGetApiWorkspaceGet({ id: workspaceId })

    const data = useGetApiChatBotDataGet({ workspaceId: workspaceId, id: chatBotDataId })
    // TODO:Limit max collections
    const collections = useGetApiEmbeddingCollectionList({ workspaceId: workspaceId, skip: 0, limit: 100 })
    const collectionsNameMap = useMemo(() => collections.data?.data.results?.map(x => {
        return {
            label: `${x.name}(${x.id})`,
            value: x.id
        } as ComboboxItem
    }) ?? [], [collections.data?.data.results])
    const { confirmAndDelete } = useDeleteChatBotData()
    const { renameChatBotData } = useRenameChatBotData()

    // const updateLineBot_Mutation = usePutApiChatBotDataUpdateLineBotConfigs()
    const updateChatConfig_Mutation = usePutApiChatBotDataUpdateChatConfig()
    // const updateChatBotConfig_Mutation = usePutApiChatBotDataUpdateChatBotConfig()
    const updateTestConfig_Mutation = usePutApiChatBotDataUpdateTestConfig()
    const updateTagsConfig_Mutation = usePutApiChatBotDataUpdateTagsConfig()
    async function GetLatestData() {
        var result = await getApiChatBotDataGet({ workspaceId: workspaceId, id: chatBotDataId });
        return result.data
    }
    return <Box pos="relative">
        <LoadingOverlay visible={data.isFetching} zIndex={1000} overlayProps={{ radius: "sm", blur: 2 }} />
        <AppBreadcrumbs
            items={[
                {
                    label: '工作空間列表',
                    linkProps: { to: '/workspace' },
                },
                {
                    label: workspace.data?.data.name ?? '',
                    linkProps: { to: '/workspace/$workspaceId', params: { workspaceId } },
                },
                {
                    label: "機器人設定",
                    linkProps: { to: '/workspace/$workspaceId/chatBotData', params: { workspaceId } },
                },
                {
                    label: data.data?.data.name,
                    active: true,
                }
            ]}
        />
        <Group gap={5} mb={10} justify="space-between">
            <Group gap={5}>
                <PageTitle text={data.data?.data.name ?? ""} />
                <Menu shadow="md" position="bottom-start">
                    <Menu.Target>
                        <ActionIcon color="gray" size="sm" variant="subtle">
                            <IconDots />
                        </ActionIcon>
                    </Menu.Target>
                    <Menu.Dropdown>
                        <Menu.Item leftSection={<IconEdit size={14} />} color="var(--mantine-primary-color-filled)" onClick={async () => {
                            if (data.data?.data)
                                renameChatBotData(data.data?.data)
                        }}>
                            修改名稱
                        </Menu.Item>
                        <Menu.Item leftSection={<IconFileExport size={14} />} color="var(--mantine-primary-color-filled)" onClick={async () => {
                            var exportData = await postApiChatBotDataExport({ workspaceId: workspaceId, id: chatBotDataId })
                            TextSaveToFile(`${data.data?.data.name}.json`, JSON.stringify(exportData.data))
                        }}>
                            匯出
                        </Menu.Item>
                        <Menu.Item leftSection={<IconTrash size={14} />} color="red" onClick={async () => {
                            if (data.data?.data)
                                confirmAndDelete(data.data?.data, () => {
                                    navigate({
                                        to: "/workspace/$workspaceId/chatBotData",
                                        params: {
                                            workspaceId: workspaceId
                                        }
                                    })
                                })
                        }}>
                            刪除
                        </Menu.Item>
                    </Menu.Dropdown>
                </Menu>
            </Group>
        </Group>
        <Group>
            <Button leftSection={<IconFlask />} onClick={() => {
                navigate({
                    to: "/workspace/$workspaceId/chatBotData/$chatBotDataId/chat", params: {
                        workspaceId: workspaceId,
                        chatBotDataId: chatBotDataId
                    }
                })
            }}>測試</Button>
            {
                data.data?.data ? <>
                    <ChatConfigEditButton config={data.data?.data.chatConfig} onChange={async (v) => {
                        try {
                            await updateChatConfig_Mutation.mutateAsync({
                                params: {
                                    workspaceId: workspaceId,
                                    id: chatBotDataId
                                },
                                data: v
                            })
                            queryClient.invalidateQueries({ queryKey: data.queryKey })
                            notifications.show({ message: "修改成功" })

                        } catch (e) {
                            notifications.show({ message: "修改失敗", color: "red" })
                        }
                    }}>
                        <Button leftSection={<IconMessages />}>
                            修改聊天設定
                        </Button>
                    </ChatConfigEditButton>
                </> : <></>
            }
            {/* {
                data.data?.data ? <>
                    <ChatBotConfigEditButton config={data.data?.data.chatBotConfig} onChange={async (v) => {
                        try {
                            await updateChatBotConfig_Mutation.mutateAsync({
                                params: {
                                    workspaceId: workspaceId,
                                    id: chatBotDataId
                                },
                                data: v
                            })
                            queryClient.invalidateQueries({ queryKey: data.queryKey })
                            notifications.show({ message: "修改成功" })

                        } catch (e) {
                            notifications.show({ message: "修改失敗", color: "red" })
                        }
                    }}>
                        <Button leftSection={<IconRobot />}>
                            修改機器人設定
                        </Button>
                    </ChatBotConfigEditButton>
                </> : <></>
            } */}
            {
                data.data?.data ? <>
                    <TagsConfigEditButton config={data.data?.data.tagsConfig} workspaceId={workspaceId} embeddingCollectionId={data.data?.data.embeddingCollectionConfigs?.map(x => x.embeddingCollectionId ?? "").filter(x => x)} onChange={async (v) => {
                        try {
                            await updateTagsConfig_Mutation.mutateAsync({
                                params: {
                                    workspaceId: workspaceId,
                                    id: chatBotDataId
                                },
                                data: v
                            })
                            queryClient.invalidateQueries({ queryKey: data.queryKey })
                            notifications.show({ message: "修改成功" })

                        } catch (e) {
                            notifications.show({ message: "修改失敗", color: "red" })
                        }
                    }}>
                        <Button leftSection={<IconTags />}>
                            修改Tags設定
                        </Button>
                    </TagsConfigEditButton>
                </> : <></>
            }
            {
                data.data?.data ? <>
                    <TestConfigEditButton workspaceId={workspaceId} chatBotDataId={chatBotDataId} config={data.data?.data.testConfig} onChange={async (v) => {
                        try {
                            await updateTestConfig_Mutation.mutateAsync({
                                params: {
                                    workspaceId: workspaceId,
                                    id: chatBotDataId
                                },
                                data: v
                            })
                            queryClient.invalidateQueries({ queryKey: data.queryKey })
                            notifications.show({ message: "修改成功" })

                        } catch (e) {
                            notifications.show({ message: "修改失敗", color: "red" })
                        }
                    }}>
                        <Button leftSection={<IconMessageReport />}>
                            修改測試設定
                        </Button>
                    </TestConfigEditButton>
                </> : <></>
            }
        </Group>
        <Text size="xl" fw={600}>連結資料庫:</Text>
        <Group>
            <Button leftSection={<IconDatabasePlus />} onClick={() => {
                modals.openContextModal({
                    modal: "TextInput",
                    title: "資料庫名稱",
                    innerProps: {
                        onConfirm: async v => {
                            if (!data.data?.data) return;
                            try {
                                await postApiChatBotDataCreateAndConnectEmbeddingCollection({
                                    workspaceId: workspaceId,
                                    id: chatBotDataId,
                                    embeddingCollectionName: v
                                })
                                queryClient.invalidateQueries({ queryKey: data.queryKey })
                                notifications.show({ message: "創建成功" })

                            } catch (e) {
                                notifications.show({ message: "創建失敗", color: "red" })

                            }
                        }
                    }
                })
            }}>創建並連結資料庫</Button>
            <Button leftSection={<IconDatabaseShare />} onClick={() => {
                modals.openContextModal({
                    modal: "Autocomplete",
                    title: "資料庫Id",
                    innerProps: {
                        data: collectionsNameMap.filter(x => (data.data?.data.embeddingCollectionConfigs?.findIndex(f => f.embeddingCollectionId == x.value) ?? -1) < 0),
                        onConfirm: async v => {
                            if (!data.data?.data) return;
                            if (data.data.data.embeddingCollectionConfigs?.find(x => x.embeddingCollectionId == v)) {
                                notifications.show({ message: "資料庫已經連結", color: "yellow" })
                                return;
                            }
                            try {
                                var collection = await getApiEmbeddingCollectionGet({
                                    workspaceId: workspaceId,
                                    id: v
                                })
                            } catch (e) {
                                notifications.show({ message: "資料庫驗證失敗", color: "red" })
                                return;
                            }
                            if (!collection.data.id) {
                                notifications.show({ message: "資料庫不存在", color: "red" })
                                return
                            }
                            try {
                                await postApiChatBotDataConnectEmbeddingCollection({
                                    workspaceId: workspaceId,
                                    id: chatBotDataId,
                                    embeddingCollectionId: v
                                })
                                queryClient.invalidateQueries({ queryKey: data.queryKey })
                                notifications.show({ message: "連結成功" })

                            } catch (e) {
                                notifications.show({ message: "連結失敗", color: "red" })

                            }
                        }
                    }
                })
            }}>連結資料庫</Button>
        </Group >
        <Stack>
            {data.data?.data.embeddingCollectionConfigs?.map((x, idx) => <Box key={`${x.embeddingCollectionId}`}>
                {x.embeddingCollectionId && <ChatBotEmbeddingCollectionEditItem workspaceId={workspaceId} chatBotDataId={chatBotDataId} embeddingCollectionId={x.embeddingCollectionId} />}
            </Box>)}
        </Stack>
    </Box >
}